<template>
  <section>
    <!-- Skeleton: 4 placeholder tiles that match the real grid shape so the
         section doesn't reflow when data arrives. -->
    <div
      v-if="tiles.length === 0 && isLoading"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5"
      aria-hidden="true"
    >
      <div
        v-for="i in 4"
        :key="`pulse-skel-${i}`"
        class="rounded-theme-md border border-theme-border dark:border-theme-border
          bg-theme-surface dark:bg-theme-surface p-4 flex flex-col gap-1.5"
        style="min-height: 7rem"
      >
        <skeleton-text width="60%" height="10px" />
        <skeleton-text width="55%" height="22px" />
        <skeleton-text width="40%" height="10px" />
        <div class="mt-auto -mx-4 -mb-4 h-7">
          <skeleton-text width="100%" height="100%" variant="rectangle" />
        </div>
      </div>
    </div>

    <div
      v-else-if="tiles.length === 0"
      class="rounded-theme-md border border-dashed border-theme-border dark:border-theme-border p-3 text-center text-xs text-theme-text-secondary"
    >
      {{ t('home_cockpit.layer1.empty') }}
    </div>

    <!-- Pulse strip: at most 4 cards, equal-width across the available space.
         Stacks on small viewports so the values stay readable. -->
    <div
      v-else
      class="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-4"
    >
      <kpi-card
        v-for="tile in visibleTiles"
        :key="tile.tile_key"
        data-test="pulse-tile"
        :data-metric="tile.tile_key"
        :kpi-value="tile.kpi_value"
        :label="tile.label"
        :unit="tile.unit"
        :value="tile.value"
        :error="tile.error"
        :stale-since="tile.stale_since"
        :to="tile.deep_link"
      />
    </div>
  </section>
</template>

<script setup>
  /* eslint-disable no-undef */
  import KpiCard from '@/components/ui/data-display/KpiCard.vue';
  import SkeletonText from '@/components/ui/states/SkeletonText.vue';
  import { loadPulse, usePulse } from '@/composables/pulse';
  import { markCriticalContentSettled } from '@/utils/deferNonCritical';
  import { computed, onBeforeUnmount, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  const REFRESH_MS = 60_000;
  // Hard cap on the number of tiles rendered. The Home strip is meant to be a
  // glanceable snapshot of the user's most-important markets — anything past
  // the fourth card asks the user to scroll *and* divides attention. Keep it
  // intentional and well-suited to a single screen width.
  const MAX_TILES = 4;

  const props = defineProps({ demo: Boolean });
  const refresh = () => loadPulse({ demo: props.demo });
  const { tiles, isLoading } = usePulse();
  const { t } = useI18n();

  const visibleTiles = computed(() => (tiles.value || []).slice(0, MAX_TILES));

  let timer = null;

  onMounted(async () => {
    await refresh();
    // The main content is in: let deferred non-critical calls go out.
    markCriticalContentSettled();
    timer = setInterval(() => {
      refresh();
    }, REFRESH_MS);
  });

  onBeforeUnmount(() => {
    if (timer) clearInterval(timer);
  });

  defineExpose({ refresh });
</script>
